import React, { useState } from "react";
import { View, Text, StyleSheet, Dimensions } from "react-native";
import {
  VictoryChart,
  VictoryAxis,
  VictoryBar,
  VictoryLine,
  VictoryScatter,
  VictoryGroup,
} from "victory";
import Colors from "../../../Resources/Color";
import { Ionicons } from "@expo/vector-icons";
import { observer } from "mobx-react";
import Tooltip from "../../Components/Tooltip";
import { useTranslation } from "react-i18next";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const SAMPLE_DATA = [
  {
    day: "Mon",
    leg: 2,
    push: 1,
    pull: 2,
    sleep: 3,
    recovery: 2,
    motivation: 4,
    tech: 5,
  },
  {
    day: "Tue",
    leg: 5,
    push: 4,
    pull: 3,
    sleep: 2,
    recovery: 1,
    motivation: 3,
    tech: 3,
  },
  // {
  //   day: "Wed",
  //   leg: 2,
  //   push: 1,
  //   pull: 2,
  //   sleep: 5,
  //   recovery: 5,
  //   motivation: 5,
  //   tech: 3,
  // },
  // {
  //   day: "Thu",
  //   leg: 1,
  //   push: 1,
  //   pull: 1,
  //   sleep: 4,
  //   recovery: 3,
  //   motivation: 2,
  //   tech: 5,
  // },
  {
    day: "Fri",
    leg: 4,
    push: 2,
    pull: 3,
    sleep: 2,
    recovery: 3,
    motivation: 3,
    tech: 4,
  },
  {
    day: "Sat",
    leg: 5,
    push: 5,
    pull: 4,
    sleep: 5,
    recovery: 2,
    motivation: 5,
    tech: 2,
  },
  // {
  //   day: "Sun",
  //   leg: 2,
  //   push: 4,
  //   pull: 2,
  //   sleep: 4,
  //   recovery: 5,
  //   motivation: 4,
  //   tech: 4,
  // },
];

const TracCard = observer(({ client }) => {
  const { t } = useTranslation();
  const [layout, setLayout] = useState({ width: 450, height: 0 });

  const onLayout = (event) => {
    const { width } = event.nativeEvent.layout;
    setLayout({ width, height: 0 });
  };

  const getTooltipContent = (metricPrefix, inverted = false) => {
    const order = [5, 4, 3, 2, 1];
    return order
      .map((i) => {
        const labelIndex = inverted ? 6 - i : i;
        return `${i} - ${t(`${metricPrefix}${labelIndex}-string`)}`;
      })
      .join("\n");
  };

  const chartWidth = layout.width || Dimensions.get("window").width - 40;
  const commonPadding = { left: 20, right: 10, top: 10, bottom: 30 };
  const topChartPadding = { ...commonPadding, bottom: 5 }; // Minimal bottom padding for top chart

  if (!client || !client.summaryStartDate) {
    return null;
  }

  const summaryStartDate = new Date(client.summaryStartDate);
  const summaryEndDate = new Date(summaryStartDate);
  summaryEndDate.setDate(summaryEndDate.getDate() + 7);

  const weekTracs = (client.tracs || []).filter((trac) => {
    if (!trac.date) return false;
    const tracDate = new Date(trac.date);
    return tracDate >= summaryStartDate && tracDate < summaryEndDate;
  });

  const weekDays = DAYS.map((label, index) => {
    const date = new Date(summaryStartDate);
    date.setDate(date.getDate() + index);
    return { label, date };
  });

  const averageForKey = (entries, key) => {
    if (!entries || entries.length === 0) return null;
    const total = entries.reduce((sum, entry) => sum + (entry[key] ?? 0), 0);
    return total / entries.length;
  };

  const weeklyData = weekDays.map((day) => {
    const dayEntries = weekTracs.filter((trac) => {
      const tracDate = new Date(trac.date);
      return (
        tracDate.getFullYear() === day.date.getFullYear() &&
        tracDate.getMonth() === day.date.getMonth() &&
        tracDate.getDate() === day.date.getDate()
      );
    });

    const leg = averageForKey(dayEntries, "legSoreness");
    const push = averageForKey(dayEntries, "pushSoreness");
    const pull = averageForKey(dayEntries, "pullSoreness");

    return {
      day: day.label,
      leg: leg !== null && leg > 0 ? 6 - leg : null,
      push: push !== null && push > 0 ? 6 - push : null,
      pull: pull !== null && pull > 0 ? 6 - pull : null,
      sleep: averageForKey(dayEntries, "sleepQualityNutrition"),
      recovery: averageForKey(dayEntries, "perceivedRecovery"),
      motivation: averageForKey(dayEntries, "motivation"),
      tech: averageForKey(dayEntries, "technicalComfort"),
    };
  });

  const hasWeekData = weekTracs.length > 0;
  const chartData = hasWeekData ? weeklyData : SAMPLE_DATA;

  const getVictoryData = (key) =>
    chartData
      .filter((d) => d[key] !== null)
      .map((d) => ({ x: d.day, y: d[key] }));

  return (
    <View style={styles.card} onLayout={onLayout}>
      <View style={styles.cardHeader}>
        <View style={styles.cardHeaderMain}>
          <View style={styles.cardIcon}>
            <Ionicons name="pulse-outline" size={20} color={Colors.grayColor} />
          </View>
          <Text style={styles.cardTitle}>TRAC</Text>
        </View>
      </View>

      <View style={styles.chartsWrapper}>
        <View
          style={!hasWeekData ? styles.sampleDataContainer : undefined}
          pointerEvents={!hasWeekData ? "none" : "auto"}
        >
          {/* Soreness Chart (Bar Chart) */}
          <View style={styles.chartContainer}>
            <View style={styles.chartHeader}>
              <Text style={styles.chartTitle}>{t("soreness-string")}</Text>
              <View style={styles.legendContainer}>
                {[
                  {
                    label: t("legSoreness-string"),
                    color: Colors.bubbleColor,
                    metric: "soreness",
                  },
                  {
                    label: t("pushSoreness-string"),
                    color: Colors.bodyweightColor,
                    metric: "soreness",
                  },
                  {
                    label: t("pullSoreness-string"),
                    color: Colors.tracColor,
                    metric: "soreness",
                  },
                ].map((item, index) => (
                  <Tooltip
                    key={index}
                    content={getTooltipContent(item.metric, true)}
                    placement="top"
                    maxWidth={300}
                  >
                    <View style={styles.legendItem}>
                      <View
                        style={[
                          styles.legendDot,
                          { backgroundColor: item.color },
                        ]}
                      />
                      <Text style={styles.legendText}>{item.label}</Text>
                    </View>
                  </Tooltip>
                ))}
              </View>
            </View>

            {layout.width > 0 && (
              <VictoryChart
                width={chartWidth}
                height={140}
                padding={topChartPadding}
                domain={{ y: [0, 5] }}
                domainPadding={{ x: 30 }} // Add spacing between groups
                categories={{ x: DAYS }}
              >
                <VictoryAxis
                  style={{
                    axis: { stroke: "transparent" },
                    ticks: { stroke: "transparent" },
                    tickLabels: {
                      fill: "transparent",
                      fontFamily:
                        "system-ui, -apple-system, 'Segoe UI', sans-serif",
                    }, // Hide labels
                    grid: { stroke: "transparent" },
                  }}
                />
                <VictoryAxis
                  dependentAxis
                  tickValues={[1, 2, 3, 4, 5]}
                  style={{
                    axis: { stroke: "transparent" },
                    grid: {
                      stroke: Colors.borderColor,
                      strokeDasharray: "4, 4",
                    },
                    tickLabels: {
                      fontSize: 10,
                      fill: Colors.grayColor,
                      padding: 5,
                      fontFamily:
                        "system-ui, -apple-system, 'Segoe UI', sans-serif",
                    },
                  }}
                />
                <VictoryGroup
                  offset={10}
                  colorScale={[
                    Colors.bubbleColor,
                    Colors.bodyweightColor,
                    Colors.tracColor,
                  ]}
                >
                  <VictoryBar
                    data={getVictoryData("leg")}
                    cornerRadius={{ top: 2 }}
                    barWidth={8}
                  />
                  <VictoryBar
                    data={getVictoryData("push")}
                    cornerRadius={{ top: 2 }}
                    barWidth={8}
                  />
                  <VictoryBar
                    data={getVictoryData("pull")}
                    cornerRadius={{ top: 2 }}
                    barWidth={8}
                  />
                </VictoryGroup>
              </VictoryChart>
            )}
          </View>

          {/* Recovery Chart (Line Chart) */}
          <View style={styles.chartContainer}>
            <View style={styles.chartHeader}>
              <Text style={styles.chartTitle}>
                {t("recoveryContext-string")}
              </Text>
              <View style={styles.legendContainer}>
                {[
                  {
                    label: t("sleepQualityNutrition-string"),
                    color: Colors.bubbleColor,
                    metric: "sleepQuality",
                  },
                  {
                    label: t("perceivedRecovery-string"),
                    color: Colors.proteinColor,
                    metric: "perceivedRecovery",
                  },
                  {
                    label: t("motivation-string"),
                    color: Colors.noteColor,
                    metric: "motivation",
                  },
                  {
                    label: t("technicalComfort-string"),
                    color: Colors.grayColor,
                    metric: "technicalComfort",
                  },
                ].map((item, index) => (
                  <Tooltip
                    key={index}
                    content={getTooltipContent(item.metric)}
                    placement="top"
                    maxWidth={300}
                  >
                    <View style={styles.legendItem}>
                      <View
                        style={[
                          styles.legendDot,
                          { backgroundColor: item.color },
                        ]}
                      />
                      <Text style={styles.legendText}>{item.label}</Text>
                    </View>
                  </Tooltip>
                ))}
              </View>
            </View>

            {layout.width > 0 && (
              <VictoryChart
                width={chartWidth}
                height={160}
                padding={commonPadding}
                domain={{ y: [0, 5] }}
                domainPadding={{ x: 30 }} // Match domain padding of bar chart for alignment
                categories={{ x: DAYS }}
              >
                <VictoryAxis
                  tickValues={DAYS}
                  style={{
                    axis: { stroke: Colors.lightGrayColor },
                    tickLabels: {
                      fontSize: 11,
                      fill: Colors.grayColor,
                      padding: 5,
                      fontFamily:
                        "system-ui, -apple-system, 'Segoe UI', sans-serif",
                    },
                    grid: { stroke: Colors.lightGrayColor, strokeWidth: 1 }, // Vertical grid lines
                  }}
                />
                <VictoryAxis
                  dependentAxis
                  tickValues={[1, 2, 3, 4, 5]}
                  style={{
                    axis: { stroke: "transparent" },
                    grid: {
                      stroke: Colors.borderColor,
                      strokeDasharray: "4, 4",
                    },
                    tickLabels: {
                      fontSize: 10,
                      fill: Colors.grayColor,
                      padding: 5,
                      fontFamily:
                        "system-ui, -apple-system, 'Segoe UI', sans-serif",
                    },
                  }}
                />

                {/* Render lines in reverse order to ensure Sleep (first in legend) is on top if needed, 
                  or just ensure distinct colors. 
                  Order: Tech, Motivation, Recovery, Sleep (Sleep on top) */}
                {[
                  { key: "tech", color: Colors.grayColor },
                  { key: "motivation", color: Colors.noteColor },
                  { key: "recovery", color: Colors.proteinColor },
                  { key: "sleep", color: Colors.bubbleColor },
                ].map((metric) => (
                  <VictoryGroup
                    key={metric.key}
                    data={getVictoryData(metric.key)}
                  >
                    <VictoryLine
                      interpolation="monotoneX"
                      style={{ data: { stroke: metric.color, strokeWidth: 2 } }}
                    />
                    <VictoryScatter
                      size={4}
                      style={{
                        data: {
                          fill: metric.color,
                          stroke: Colors.whiteColor,
                          strokeWidth: 2,
                        },
                      }}
                    />
                  </VictoryGroup>
                ))}
              </VictoryChart>
            )}
          </View>
        </View>

        {!hasWeekData && (
          <View style={styles.overlay}>
            <View style={styles.overlayContent}>
              <Ionicons
                name="pulse-outline"
                size={24}
                color={Colors.grayColor}
              />
              <Text style={styles.overlayText}>{t("noDataYet-string")}</Text>
              <Text style={styles.overlaySubText}>
                {t("tracTrendsInfo-string")}
              </Text>
            </View>
          </View>
        )}
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  card: {
    backgroundColor: Colors.whiteColor,
    borderRadius: 14,
    padding: 18,
    borderWidth: 0.1,
    borderColor: Colors.borderColor,
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  cardHeaderMain: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  cardIcon: {
    backgroundColor: Colors.lightGrayColor,
    padding: 6,
    borderRadius: 10,
  },
  cardTitle: {
    fontSize: 12,
    fontWeight: "600",
    letterSpacing: 0.5,
    color: Colors.grayColor,
  },
  chartsWrapper: {
    gap: 0,
  },
  chartContainer: {
    marginBottom: 0,
  },
  chartHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 10,
    // paddingRight: 10,
    gap: 12,
  },
  chartTitle: {
    fontSize: 13,
    fontWeight: "600",
    color: Colors.darkgrayColor,
    flexShrink: 1,
    maxWidth: 120,
  },
  legendContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    columnGap: 12,
    rowGap: 2,
    justifyContent: "flex-end",
    flexShrink: 1,
  },
  legendItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  legendText: {
    fontSize: 11,
    color: Colors.grayColor,
    fontWeight: "500",
  },
  tracEmptyState: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 20,
    paddingHorizontal: 16,
    gap: 8,
  },
  tracEmptyTitle: {
    fontSize: 13,
    fontWeight: "600",
    color: Colors.darkgrayColor,
  },
  tracEmptyDescription: {
    fontSize: 12,
    color: Colors.grayColor,
    textAlign: "center",
    lineHeight: 16,
  },
  sampleDataContainer: {
    opacity: 0.3,
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1,
  },
  overlayContent: {
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 14,
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    gap: 8,
  },
  overlayText: {
    fontSize: 13,
    fontWeight: "600",
    color: Colors.darkgrayColor,
  },
  overlaySubText: {
    fontSize: 11,
    color: Colors.grayColor,
    marginTop: 2,
    maxWidth: 190,
    textAlign: "center",
  },
});

export default TracCard;
